import { SCORM_MESSAGE_KINDS } from '../runtime/scormMessages';

export function buildScormShim(playerOrigin: string): string {
  return `/* Libertify SCORM 1.2 shim — runs inside the LMS-served entry HTML.
   Bridges the cross-origin media-player iframe to the LMS \`window.API\`. */
(function () {
  var KINDS = ${JSON.stringify(SCORM_MESSAGE_KINDS)};
  var PLAYER_ORIGIN = ${JSON.stringify(playerOrigin)};
  var MAX_API_LOOKUPS = 10;
  var COMMIT_DEBOUNCE_MS = 2000;

  function findApi(win) {
    var depth = 0;
    while (win && depth < MAX_API_LOOKUPS) {
      try {
        if (win.API) return win.API;
      } catch (_) {}
      if (win === win.parent) break;
      win = win.parent;
      depth++;
    }
    try {
      if (window.opener && window.opener.API) return window.opener.API;
    } catch (_) {}
    return null;
  }

  var api = findApi(window.parent);
  var initialized = false;
  var completed = false;
  var finished = false;
  var commitTimer = null;
  var iframeWindow = null;
  // SCORM 1.2 caps cmi.suspend_data at 4096 characters.
  var MAX_SUSPEND_DATA = 4096;

  // Active-time accounting for cmi.core.session_time. We accrue elapsed time
  // only while the tab is visible, so a backgrounded or left-open SCORM window
  // does not inflate the reported time — Moodle (and most LMSs) ADD each
  // session's session_time into cmi.core.total_time, so wall-clock from
  // launch-to-unload would balloon total_time well beyond actual engagement.
  var activeMs = 0;
  var lastResumeAt = document.visibilityState === 'visible' ? Date.now() : null;

  function pauseTimer() {
    if (lastResumeAt !== null) {
      activeMs += Date.now() - lastResumeAt;
      lastResumeAt = null;
    }
  }
  function resumeTimer() {
    if (lastResumeAt === null) lastResumeAt = Date.now();
  }
  function activeSeconds() {
    var total =
      activeMs + (lastResumeAt !== null ? Date.now() - lastResumeAt : 0);
    return Math.max(0, total / 1000);
  }

  function log() {
    if (!window.__SCORM_DEBUG__) return;
    var args = ['[scorm-shim]'].concat(Array.prototype.slice.call(arguments));
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  function safeCall(name) {
    if (!api) return '';
    try {
      var fn = api[name];
      if (typeof fn !== 'function') return '';
      var args = Array.prototype.slice.call(arguments, 1);
      var result = fn.apply(api, args);
      log(name, args, '->', result);
      return result;
    } catch (e) {
      log('error calling', name, e);
      return '';
    }
  }

  function initialize() {
    if (initialized) return;
    safeCall('LMSInitialize', '');
    initialized = true;
    // cmi.core.entry is "resume" when the LMS retained state from a suspended
    // session (we set exit=suspend below), or "ab-initio" on a fresh start.
    // Logged for debugging; the actual resume is driven by lesson_location.
    log('entry mode:', safeCall('LMSGetValue', 'cmi.core.entry'));
    // Declare the score range once so the LMS renders score.raw as a percentage.
    safeCall('LMSSetValue', 'cmi.core.score.min', '0');
    safeCall('LMSSetValue', 'cmi.core.score.max', '100');
    // SCORM 1.2 default lesson_status is "not attempted" on first launch.
    // Setting to "incomplete" on first contact so the LMS shows the activity in progress.
    var status = safeCall('LMSGetValue', 'cmi.core.lesson_status');
    if (status === 'not attempted' || status === '') {
      safeCall('LMSSetValue', 'cmi.core.lesson_status', 'incomplete');
    }
    // Relaunch of an already-completed lesson: keep the completed state so we
    // never downgrade score/exit, and tell the player (it unlocks seeking).
    if (status === 'completed') {
      completed = true;
    }
  }

  function scheduleCommit() {
    if (commitTimer) clearTimeout(commitTimer);
    commitTimer = setTimeout(function () {
      safeCall('LMSCommit', '');
      commitTimer = null;
    }, COMMIT_DEBOUNCE_MS);
  }

  function markCompleted() {
    if (completed) return;
    completed = true;
    safeCall('LMSSetValue', 'cmi.core.lesson_status', 'completed');
    safeCall('LMSSetValue', 'cmi.core.score.raw', '100');
    // Normal exit — the attempt is finished, not suspended.
    safeCall('LMSSetValue', 'cmi.core.exit', '');
    safeCall('LMSSetValue', 'cmi.core.session_time', formatTime(activeSeconds()));
    if (commitTimer) {
      clearTimeout(commitTimer);
      commitTimer = null;
    }
    safeCall('LMSCommit', '');
    // LMSFinish is deliberately deferred to unload: the learner may keep
    // watching, and the LMS must still receive the final session_time and
    // lesson_location (further LMSSetValue calls are invalid after LMSFinish).
  }

  function finish() {
    if (finished) return;
    finished = true;
    if (commitTimer) clearTimeout(commitTimer);
    pauseTimer();
    safeCall('LMSSetValue', 'cmi.core.session_time', formatTime(activeSeconds()));
    if (completed) {
      safeCall('LMSSetValue', 'cmi.core.exit', '');
    } else {
      // Incomplete exit: ask the LMS to preserve resume state so the next
      // launch reports cmi.core.entry="resume" instead of starting fresh.
      safeCall('LMSSetValue', 'cmi.core.exit', 'suspend');
    }
    safeCall('LMSCommit', '');
    safeCall('LMSFinish', '');
  }

  function formatTime(totalSeconds) {
    // Work in floored centiseconds so rounding can never carry the seconds
    // field up to 60 (e.g. 59.999s) — SCORM 1.2 CMITimespan requires SS < 60
    // and a strict LMS rejects the whole session_time write otherwise.
    var totalCentis = Math.floor(totalSeconds * 100);
    var hours = Math.floor(totalCentis / 360000);
    var minutes = Math.floor((totalCentis % 360000) / 6000);
    var centis = totalCentis % 6000;
    var ss = (centis / 100).toFixed(2);
    if (centis < 1000) ss = '0' + ss;
    return pad(hours) + ':' + pad(minutes) + ':' + ss;
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function postToPlayer(message) {
    if (!iframeWindow) return;
    try {
      iframeWindow.postMessage(message, PLAYER_ORIGIN);
    } catch (e) {
      log('postToPlayer failed', e);
    }
  }

  window.addEventListener('message', function (event) {
    if (event.origin !== PLAYER_ORIGIN) return;
    var data = event.data;
    if (!data || typeof data !== 'object' || !data.kind) return;
    iframeWindow = event.source;

    switch (data.kind) {
      case KINDS.INIT:
        initialize();
        var location = safeCall('LMSGetValue', 'cmi.core.lesson_location');
        var parsed = parseFloat(location);
        var suspendData = safeCall('LMSGetValue', 'cmi.suspend_data');
        postToPlayer({
          kind: KINDS.RESUME_RESPONSE,
          lessonLocation: isNaN(parsed) ? 0 : parsed,
          suspendData: typeof suspendData === 'string' ? suspendData : '',
          completed: completed,
        });
        break;

      case KINDS.SET_LOCATION:
        if (finished) return;
        if (!initialized) initialize();
        if (typeof data.lessonLocation === 'number') {
          safeCall('LMSSetValue', 'cmi.core.lesson_location', String(data.lessonLocation));
        }
        if (
          typeof data.suspendData === 'string' &&
          data.suspendData.length > 0 &&
          data.suspendData.length <= MAX_SUSPEND_DATA
        ) {
          safeCall('LMSSetValue', 'cmi.suspend_data', data.suspendData);
        }
        // Once completed, score.raw is pinned at 100 — never let a later
        // coverage report lower it.
        if (typeof data.score === 'number' && !completed) {
          safeCall('LMSSetValue', 'cmi.core.score.raw', String(data.score));
        }
        scheduleCommit();
        break;

      case KINDS.COMPLETE:
        if (finished) return;
        if (!initialized) initialize();
        markCompleted();
        break;

      case KINDS.FLUSH:
        if (!initialized || finished) return;
        if (commitTimer) {
          clearTimeout(commitTimer);
          commitTimer = null;
        }
        safeCall('LMSCommit', '');
        break;

      default:
        log('unknown message kind', data.kind);
    }
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      pauseTimer();
      // Persist progress when the learner backgrounds the tab; they may never
      // return to fire pagehide/beforeunload.
      if (initialized && !finished) safeCall('LMSCommit', '');
    } else {
      resumeTimer();
    }
  });

  window.addEventListener('pagehide', function () { finish(); });
  window.addEventListener('beforeunload', function () { finish(); });

  if (!api && window.__SCORM_DEBUG__) {
    // eslint-disable-next-line no-console
    console.warn('[scorm-shim] LMS API not found on any parent window.');
  }
})();
`;
}
